import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Loader2 } from 'lucide-react';
import { queueTokenAnalysis } from '@/api/tokenAnalysis';
import { useTokenAnalysis } from '@/hooks/useTokenAnalysis';

interface TokenAnalysisSectionProps {
  tokenSymbol: string;
}

const POLL_INTERVAL_MS = 5000;

/**
 * Converts literal URLs in text to markdown links with "link" as the display text
 * Also normalizes existing markdown links to use "link" as display text
 */
const formatLinksInText = (text: string): string => {
  // First, normalize existing markdown links to use "link" as display text
  // Matches [anything](url) and replaces with [link](url)
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let result = text.replace(markdownLinkRegex, '[link]($2)');
  
  // Then, convert any remaining plain URLs to markdown format
  // Negative lookbehind (?<!\]\() ensures URL is not preceded by ](
  // [^\s)]+ matches URL characters, excluding spaces and closing parens
  const plainUrlRegex = /(?<!\]\()(https?:\/\/[^\s)]+)/g;
  result = result.replace(plainUrlRegex, '[link]($&)');
  
  return result;
};

/**
 * Custom link component for ReactMarkdown that renders with underline styling
 */
const UnderlinedLink: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = ({ href, children, ...props }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noopener noreferrer"
    className="underline text-accent-light"
    {...props}
  >
    {children}
  </a>
);

export const TokenAnalysisSection: React.FC<TokenAnalysisSectionProps> = ({ tokenSymbol }) => {
  const normalizedToken = useMemo(() => tokenSymbol?.trim(), [tokenSymbol]);
  const [hasQueued, setHasQueued] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);

  const {
    data: tokenAnalysis,
    isLoading,
    isFetching,
    refetch,
  } = useTokenAnalysis({
    tokenName: normalizedToken,
    enabled: !!normalizedToken,
  });

  const hasDescription = !!tokenAnalysis?.description;
  const isGenerating = hasQueued && !hasDescription;

  // Reset queue state when token changes
  useEffect(() => {
    setHasQueued(false);
    setQueueError(null);
  }, [normalizedToken]);

  // Queue analysis if none exists
  useEffect(() => {
    const shouldQueue =
      !!normalizedToken &&
      !hasDescription &&
      !isLoading &&
      !isFetching &&
      !hasQueued &&
      !queueError;

    if (!shouldQueue) {
      return;
    }

    queueTokenAnalysis({ tokenName: normalizedToken })
      .then(() => setHasQueued(true))
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Failed to queue analysis';
        setQueueError(message);
      });
  }, [normalizedToken, hasDescription, isLoading, isFetching, hasQueued, queueError]);

  // Poll for analysis while generating
  useEffect(() => {
    if (!isGenerating) {
      return;
    }

    const intervalId = setInterval(() => {
      refetch();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isGenerating, refetch]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-text-primary">AI Token Analysis</h3>
        {(isLoading || isFetching || isGenerating) && (
          <Loader2 className="w-4 h-4 animate-spin text-accent" />
        )}
      </div>

      {queueError && (
        <p className="text-sm text-red-400 bg-primary/20 rounded-lg p-3">
          {queueError}
        </p>
      )}

      {hasDescription ? (
        <div className="prose prose-invert prose-sm max-w-none bg-primary/40 border border-gray-500/30 rounded-lg p-3">
          <ReactMarkdown
            components={{
              a: UnderlinedLink,
            }}
          >
            {formatLinksInText(tokenAnalysis?.description || '')}
          </ReactMarkdown>
        </div>
      ) : (
        <p className="text-sm text-text-secondary bg-primary/20 rounded-lg p-3">
          {isGenerating
            ? 'Generating AI analysis...'
            : 'No AI analysis is available yet for this token.'}
        </p>
      )}
    </div>
  );
};


